import { useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { AlertCircle, CheckCheck, ChevronDown, ChevronUp, Filter, MailOpen, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { statusApi } from "../../api/status";
import { EmptyState, LoadingText, Pagination, SearchInput, Select } from "../ui";
import { IncidentBadge, SeverityBadge } from "./badges";
import { timeAgo, formatDateTime, incidentDuration } from "./statusFormat";
import type { DrillFilter, IncidentsResult, Platform } from "../../types/status";

export default function IncidentsTab({
  isAdmin,
  drillFilter,
  onClearDrill,
  platform,
}: {
  isAdmin: boolean;
  drillFilter: DrillFilter | null;
  onClearDrill: () => void;
  platform?: Platform;
}) {
  const [incidents, setIncidents] = useState<IncidentsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("critical,high");
  const [fromDt, setFromDt] = useState("");
  const [toDt, setToDt] = useState("");
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState("started_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const navigate = useNavigate();

  useEffect(() => { setPage(1); }, [drillFilter]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const params: Record<string, unknown> = { page, page_size: 20, sort: sortField, dir: sortDir };
        if (platform) params.platform = platform;
        if (drillFilter) {
          if (drillFilter.ids?.length) {
            params.ids = drillFilter.ids.join(",");
          } else {
            if (drillFilter.from) params.from_dt = drillFilter.from;
            if (drillFilter.to) params.to_dt = drillFilter.to;
            if (drillFilter.severity) params.severity = drillFilter.severity;
            if (drillFilter.serviceType) params.service_type = drillFilter.serviceType;
            if (drillFilter.status) params.status = drillFilter.status;
          }
        } else {
          if (statusFilter) params.status = statusFilter;
          if (severityFilter) params.severity = severityFilter;
          if (fromDt) params.from_dt = fromDt;
          if (toDt) params.to_dt = toDt;
        }
        if (search) params.service = search;
        const res = await statusApi.getIncidents(params);
        setIncidents(res.data);
      } catch {
        setError("Failed to load incidents.");
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [search, statusFilter, severityFilter, fromDt, toDt, page, sortField, sortDir, drillFilter, refreshToken, platform]);

  function toggleSort(field: string) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
    setPage(1);
  }

  async function handleMarkAllRead() {
    try {
      await statusApi.markAllIncidentsRead();
      setRefreshToken((t) => t + 1);
    } catch {
      // non-critical
    }
  }

  function sortHeader(label: string, field: string) {
    const active = sortField === field;
    return (
      <button onClick={() => toggleSort(field)} className="flex items-center gap-1 uppercase tracking-wider hover:text-gray-700">
        {label}
        {active && (sortDir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
      </button>
    );
  }

  const handleFilterChange = (setter: Dispatch<SetStateAction<string>>) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setter(e.target.value);
    setPage(1);
  };

  async function handleResolve(id: string) {
    setActionLoading(id);
    try {
      await statusApi.resolveIncident(id);
      setRefreshToken((t) => t + 1);
    } catch {
      // backend 400s if already resolved
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm("Delete this incident? This cannot be undone.")) return;
    setActionLoading(id);
    try {
      await statusApi.deleteIncident(id);
      setRefreshToken((t) => t + 1);
    } catch {
      // silently ignore
    } finally {
      setActionLoading(null);
    }
  }

  return (
    <div>
      {drillFilter && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-blue-100 bg-blue-50 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Filter size={13} className="text-blue-500" />
            <span className="text-xs font-medium text-blue-700">{drillFilter.label}</span>
          </div>
          <button
            onClick={() => { onClearDrill(); setPage(1); }}
            className="text-xs text-blue-500 hover:text-blue-700"
          >
            Clear filters ×
          </button>
        </div>
      )}

      <div className="mb-5 flex flex-wrap gap-3">
        <div className="flex-1 min-w-[180px]">
          <SearchInput
            id="incident-search"
            label="Search incidents"
            placeholder="Search incidents…"
            value={search}
            onChange={(value) => { setSearch(value); setPage(1); }}
          />
        </div>

        {!drillFilter && (
          <>
            <Select value={statusFilter} onChange={handleFilterChange(setStatusFilter)}>
              <option value="">All statuses</option>
              <option value="ongoing">Ongoing</option>
              <option value="resolved">Resolved</option>
            </Select>

            <Select value={severityFilter} onChange={handleFilterChange(setSeverityFilter)}>
              <option value="critical,high">High &amp; Critical</option>
              <option value="">All severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </Select>

            <div className="flex items-center gap-1.5">
              <label className="text-xs text-gray-500 whitespace-nowrap">From</label>
              <input
                type="datetime-local"
                value={fromDt}
                onChange={handleFilterChange(setFromDt)}
                className="rounded-lg border border-gray-200 px-2.5 py-2 text-sm text-gray-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
              />
            </div>

            <div className="flex items-center gap-1.5">
              <label className="text-xs text-gray-500 whitespace-nowrap">To</label>
              <input
                type="datetime-local"
                value={toDt}
                onChange={handleFilterChange(setToDt)}
                className="rounded-lg border border-gray-200 px-2.5 py-2 text-sm text-gray-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
              />
            </div>

            {(search || statusFilter || severityFilter !== "critical,high" || fromDt || toDt) && (
              <button
                onClick={() => { setSearch(""); setStatusFilter(""); setSeverityFilter("critical,high"); setFromDt(""); setToDt(""); setPage(1); }}
                className="rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-500 hover:border-gray-300 hover:text-gray-700"
              >
                Clear
              </button>
            )}
          </>
        )}

        <button
          onClick={handleMarkAllRead}
          className="ml-auto flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-600 hover:border-gray-300 hover:text-gray-800"
        >
          <MailOpen size={13} />
          Mark all read
        </button>
      </div>

      {loading ? (
        <LoadingText className="py-12 text-center">Loading incidents…</LoadingText>
      ) : error ? (
        <p className="py-12 text-center text-sm text-red-500">{error}</p>
      ) : incidents?.total === 0 ? (
        <EmptyState
          bordered
          icon={<AlertCircle size={28} aria-hidden="true" />}
          title="No incidents found"
          description="Incidents are recorded automatically when a service check fails."
        />
      ) : (
        <>
          <div className="rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{sortHeader("Severity", "severity")}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">What happened</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{sortHeader("Platform", "platform")}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Sources</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{sortHeader("When", "started_at")}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{sortHeader("Status", "status")}</th>
                  {isAdmin && <th className="px-4 py-3" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {incidents?.items.map((inc) => (
                  <tr
                    key={inc.id}
                    onClick={() => navigate(`/status/incidents/${inc.id}`)}
                    className="cursor-pointer hover:bg-gray-50/60"
                  >
                    <td className="px-4 py-3">
                      <SeverityBadge severity={inc.severity} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {inc.is_read === false && (
                          <span className="h-2 w-2 shrink-0 rounded-full bg-blue-500" title="Unread" aria-label="Unread" />
                        )}
                        <p className={inc.is_read === false ? "font-semibold text-gray-900" : "font-medium text-gray-800"}>{inc.service_name}</p>
                        {(inc.alarm_count ?? 0) > 1 && (
                          <span className="whitespace-nowrap rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-[#1D345F]">
                            {inc.alarm_count} alarms
                          </span>
                        )}
                      </div>
                      {inc.error_message && (
                        <p className="mt-0.5 truncate max-w-[320px] text-xs text-gray-400" title={inc.error_message}>
                          {inc.error_message}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs font-medium text-gray-700 capitalize whitespace-nowrap">
                      {inc.platform === "shared" ? "All" : inc.platform}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(inc.sources?.length
                          ? inc.sources
                          : [inc.service_type === "url" ? "Monitored URL" : inc.service_name]
                        ).map((source) => (
                          <span
                            key={source}
                            className="whitespace-nowrap rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-500"
                          >
                            {source}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {timeAgo(inc.started_at) ?? formatDateTime(inc.started_at)}
                      <span className="mt-0.5 block text-gray-400">{incidentDuration(inc.started_at, inc.resolved_at)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <IncidentBadge status={inc.status} />
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1.5 justify-end">
                          {inc.status === "ongoing" && (
                            <button
                              onClick={() => handleResolve(inc.id)}
                              disabled={actionLoading === inc.id}
                              title="Mark as resolved"
                              className="flex items-center gap-1 rounded-md border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-100 disabled:opacity-50"
                            >
                              <CheckCheck size={11} />
                              Resolve
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(inc.id)}
                            disabled={actionLoading === inc.id}
                            title="Delete incident"
                            className="flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-medium text-gray-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                          >
                            <Trash2 size={11} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {incidents && (
            <Pagination
              page={page}
              pageSize={20}
              total={incidents.total}
              onPageChange={setPage}
              label="Incident pages"
            />
          )}
        </>
      )}
    </div>
  );
}
